import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * The one focus treatment on this site: the element's border takes the ring colour and it lifts.
 *
 * The element needs a border for this to show; give it `border border-transparent` if it has none.
 * That can't live in here — consumers that already draw a visible border would have its colour
 * clobbered by the transparent one when the two class lists merge.
 */
export const FOCUS_RING =
  'outline-none focus-visible:border-ring focus-visible:shadow-md focus-visible:ring-0';

/** Same ring, taken by a container whose child holds the focus — a card that is itself the control. */
export const FOCUS_RING_WITHIN = 'has-focus-visible:border-ring has-focus-visible:shadow-md';

/**
 * Same look for controls with no border of their own, or that sit inside `overflow-hidden` /
 * `overflow-auto` — an inset ring can't be clipped and costs no layout, and the shadow is dropped
 * because the clip would slice it in half.
 */
export const FOCUS_RING_INSET =
  'outline-none focus-visible:inset-ring focus-visible:inset-ring-ring focus-visible:ring-0';

/**
 * The same ring for inline text links, which have no box to put a border on. Rounded only while
 * focused so an unfocused link keeps its own shape.
 */
export const FOCUS_RING_TEXT = cn(FOCUS_RING_INSET, 'focus-visible:rounded-sm');

/** Inline link, uncoloured — inherits from its surroundings. */
export const LINK_BASE = cn('link-underline', FOCUS_RING_TEXT);

/** The small text CTA that sits under a form, next to the filled button. */
export const CTA_LINK_STYLE = cn('text-sm font-medium', LINK_BASE);

/** Inline brand link — legal pages, support addresses, anywhere prose links out. */
export const LINK_STYLE = cn('text-brand', LINK_BASE);

/** Quiet navigation link — header, footer. Reads as chrome, not as prose. */
export const NAV_LINK_STYLE = cn(
  'text-muted-foreground transition-colors duration-200',
  'hover:text-foreground active:text-foreground',
  FOCUS_RING_TEXT,
);

/**
 * Pointing at a card. The border comes up to full ink and the card takes the small lift — never a
 * translate: the page has enough motion in it without every card jumping under the cursor.
 *
 * Three states for one response: `hover:` compiles inside `@media (hover: hover)` and so never
 * fires on a phone, `active:` answers the press, and `focus:` holds the state after the finger
 * lifts — these cards are read rather than opened, so holding it until you touch something else
 * is the whole acknowledgement.
 *
 * That last one needs `tabIndex={-1}` on the card: focusable by pointer, never by keyboard, so it
 * adds no tab stop. `not-focus-visible` is for the cards that _are_ a tab stop (a Radix tab
 * panel), where raising the border under the keyboard ring reads as a rendering bug. A card that
 * is itself a link or a button takes neither — it navigates or acts.
 */
export const CARD_HOVER = cn(
  'transition-[box-shadow,border-color] duration-300',
  'hover:border-foreground/40 hover:shadow-lift-sm',
  'active:border-foreground/40 active:shadow-lift-sm',
  'focus:not-focus-visible:border-foreground/40 focus:not-focus-visible:shadow-lift-sm',
);

/**
 * The same three states for a card whose fill is already full ink: the border goes to brand
 * because ink on ink is nothing, and the lift is the deeper one because the page-tinted shadow
 * disappears under a dark tile. Layers over `CARD_HOVER`, never replaces it.
 *
 * Written out rather than derived from `CARD_HOVER`'s variants — Tailwind scans source text, so a
 * class assembled at runtime is a class it never generates.
 */
export const CARD_HOVER_INVERT = cn(
  'hover:border-brand hover:shadow-lift-invert',
  'active:border-brand active:shadow-lift-invert',
  'focus:not-focus-visible:border-brand focus:not-focus-visible:shadow-lift-invert',
);

/**
 * Pointing at a cell of a ruled grid — no border to raise, so it takes a wash. Pair it with
 * `group-hover:text-foreground group-active:text-foreground` on whatever inside it is muted.
 */
export const HOVER_WASH = cn(
  'group transition-colors duration-300',
  'hover:bg-foreground/[0.04] active:bg-foreground/[0.04]',
);

/** The muted copy inside a washed row or cell. `HOVER_WASH`'s other half. */
export const WASH_TEXT =
  'transition-colors duration-300 group-hover:text-foreground group-active:text-foreground';

/**
 * The same wash on a row that has no padding of its own. The negative margin and matching padding
 * are what make it read as a row rather than as a box drawn around the text: the fill reaches past
 * the measure, the rules keep their own span.
 */
export const ROW_HOVER = cn(HOVER_WASH, '-mx-4 px-4');
